export function initModels() {
    document.querySelectorAll('.module--models').forEach(initModelsModule);
}

function initModelsModule(module) {
    const carousel = module.querySelector('[data-carousel]');
    const filter = module.querySelector('[data-models-filter]');
    const emptyState = module.querySelector('[data-models-empty]');

    if (!carousel) return;

    const track = carousel.querySelector('[data-carousel-track]');
    const originalCards = [...track.querySelectorAll('.model-card')];
    const cardCount = originalCards.length;
    const isMobile = window.matchMedia('(max-width: 900px)').matches;

    // ── Filter logic (runs on ALL viewports) ──
    function applyFilter(filterValue) {
        const allOriginals = [...track.querySelectorAll('.model-card:not(.model-card--clone)')];

        allOriginals.forEach((card) => {
            const fuel = card.dataset.fuel || '';
            const matches = filterValue === 'all' || fuel === filterValue;
            card.classList.toggle('is-filtered-out', !matches);
        });

        const visibleCount = allOriginals.filter((c) => !c.classList.contains('is-filtered-out')).length;

        // Empty state
        if (visibleCount === 0) {
            carousel.hidden = true;
            if (emptyState) emptyState.hidden = false;
        } else {
            carousel.hidden = false;
            if (emptyState) emptyState.hidden = true;
        }

        // Rebuild carousel on desktop
        if (!isMobile && cardCount >= 2) {
            rebuildCarousel();
        }
    }

    if (filter) {
        const buttons = filter.querySelectorAll('.module-models__filter-btn');
        buttons.forEach((btn) => {
            btn.addEventListener('click', () => {
                buttons.forEach((b) => b.classList.remove('is-active'));
                btn.classList.add('is-active');
                applyFilter(btn.dataset.filter);
            });
        });
    }

    // ── Desktop carousel (skip on mobile) ──
    if (isMobile || cardCount < 2) return;

    const dots = carousel.querySelectorAll('[data-carousel-dots] .carousel__dot');
    const prevBtn = carousel.querySelector('[data-carousel-prev]');
    const nextBtn = carousel.querySelector('[data-carousel-next]');

    let allCards = [...track.querySelectorAll('.model-card')];
    let currentIndex = 0;
    let activeCloneCount = Math.min(cardCount, 3);
    let isLooping = true;

    function prepareClone(card) {
        const clone = card.cloneNode(true);
        clone.classList.add('model-card--clone');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('id');
        clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
        clone.querySelectorAll('img').forEach((img) => {
            img.loading = 'eager';
            img.decoding = 'sync';
        });
        return clone;
    }

    // Initial clone setup
    for (let i = 0; i < activeCloneCount; i++) {
        track.appendChild(prepareClone(originalCards[i]));
    }
    for (let i = 0; i < activeCloneCount; i++) {
        track.insertBefore(prepareClone(originalCards[cardCount - 1 - i]), track.firstChild);
    }

    allCards = [...track.querySelectorAll('.model-card')];
    currentIndex = activeCloneCount;

    function updateActive() {
        const ri = ((currentIndex - activeCloneCount) % getVisibleCount() + getVisibleCount()) % getVisibleCount();
        allCards.forEach((c) => c.classList.remove('is-active'));
        allCards[currentIndex]?.classList.add('is-active');
        dots.forEach((d, i) => d.classList.toggle('is-active', i === ri));
    }

    function getVisibleCount() {
        return allCards.filter((c) => !c.classList.contains('model-card--clone') && !c.classList.contains('is-filtered-out')).length;
    }

    function goToSlide(i, withTransition = true) {
        const card = allCards[i];
        if (!card) return;

        const viewport = carousel.querySelector('.carousel__viewport');
        const viewportWidth = viewport.offsetWidth;
        const cardWidth = card.offsetWidth;
        const offset = (viewportWidth - cardWidth) / 2 - card.offsetLeft;

        track.style.transition = withTransition
            ? 'transform 0.55s cubic-bezier(.16, 1, .3, 1)'
            : 'none';
        track.style.transform = `translateX(${offset}px)`;

        if (!withTransition) {
            void track.offsetWidth;
        }

        currentIndex = i;
        updateActive();
    }

    // Teleport after transition to clone
    let isTeleporting = false;

    track.addEventListener('transitionend', (e) => {
        if (e.propertyName !== 'transform') return;
        if (isTeleporting || !isLooping) return;

        const visCount = getVisibleCount();
        const needsTeleport = currentIndex >= activeCloneCount + visCount || currentIndex < activeCloneCount;
        if (!needsTeleport) return;

        isTeleporting = true;
        track.style.opacity = '0';

        requestAnimationFrame(() => {
            let target = currentIndex;
            if (currentIndex >= activeCloneCount + visCount) target -= visCount;
            else if (currentIndex < activeCloneCount) target += visCount;

            goToSlide(target, false);

            requestAnimationFrame(() => {
                track.style.opacity = '';
                isTeleporting = false;
            });
        });
    });

    // Navigation
    function next() {
        if (!isLooping && getVisibleCount() <= 1) return;
        goToSlide(currentIndex + 1);
    }
    function prev() {
        if (!isLooping && getVisibleCount() <= 1) return;
        goToSlide(currentIndex - 1);
    }

    prevBtn?.addEventListener('click', prev);
    nextBtn?.addEventListener('click', next);

    dots.forEach((dot, i) => {
        dot.addEventListener('click', () => goToSlide(activeCloneCount + i));
    });

    carousel.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
        if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
    });

    // Drag support
    let isPointerDown = false;
    let wasDragged = false;
    let pointerStartX = 0;
    let dragOffset = 0;
    let currentTransform = 0;
    const DRAG_THRESHOLD = 50;
    const DRAG_DETECT = 5;

    function getCurrentTransform() {
        const match = /translateX\(([^)]+)px\)/.exec(track.style.transform);
        return match ? parseFloat(match[1]) : 0;
    }

    track.addEventListener('pointerdown', (e) => {
        if (!isLooping && getVisibleCount() <= 1) return;
        if (e.target.closest('button')) return;
        isPointerDown = true;
        wasDragged = false;
        pointerStartX = e.clientX;
        dragOffset = 0;
        currentTransform = getCurrentTransform();
        track.style.transition = 'none';
    });

    track.addEventListener('pointermove', (e) => {
        if (!isPointerDown) return;
        const deltaX = e.clientX - pointerStartX;

        if (!wasDragged && Math.abs(deltaX) > DRAG_DETECT) {
            wasDragged = true;
            track.setPointerCapture(e.pointerId);
            track.style.cursor = 'grabbing';
        }

        if (wasDragged) {
            dragOffset = deltaX;
            track.style.transform = `translateX(${currentTransform + dragOffset}px)`;
        }
    });

    function endDrag() {
        if (!isPointerDown) return;
        isPointerDown = false;
        track.style.cursor = '';

        if (!wasDragged) return;

        if (Math.abs(dragOffset) > DRAG_THRESHOLD) {
            if (dragOffset < 0) next();
            else prev();
        } else {
            goToSlide(currentIndex);
        }
        dragOffset = 0;
    }

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', () => {
        isPointerDown = false;
        wasDragged = false;
        track.style.cursor = '';
    });

    // Prevent link navigation when dragging (capture phase)
    track.addEventListener('click', (e) => {
        if (wasDragged) {
            e.preventDefault();
            e.stopPropagation();
            setTimeout(() => { wasDragged = false; }, 0);
        }
    }, true);

    // Resize
    window.addEventListener('resize', () => {
        if (window.matchMedia('(max-width: 900px)').matches) {
            track.style.transform = '';
            track.style.transition = '';
        } else {
            goToSlide(currentIndex, false);
        }
    });

    // ── Desktop carousel rebuild (called by applyFilter) ──
    function rebuildCarousel() {
        // 1. Remove all clones
        track.querySelectorAll('.model-card--clone').forEach((c) => c.remove());

        // 2. Determine visible cards
        const allOriginals = [...track.querySelectorAll('.model-card:not(.model-card--clone)')];
        const visible = allOriginals.filter((c) => !c.classList.contains('is-filtered-out'));

        // CASE A: Empty state — handled by applyFilter
        if (visible.length === 0) {
            allCards = [...track.querySelectorAll('.model-card')];
            return;
        }

        // CASE B: Single card — no loop, centered, no clones
        if (visible.length === 1) {
            carousel.classList.add('is-single');
            isLooping = false;
            activeCloneCount = 0;
            allCards = [...track.querySelectorAll('.model-card')];

            const card = visible[0];
            const viewport = carousel.querySelector('.carousel__viewport');
            const viewportWidth = viewport.offsetWidth;
            const cardWidth = card.offsetWidth;
            const offset = (viewportWidth - cardWidth) / 2 - card.offsetLeft;
            track.style.transition = 'none';
            track.style.transform = `translateX(${offset}px)`;
            void track.offsetWidth;
            currentIndex = allCards.indexOf(card);
            allCards.forEach((c) => c.classList.remove('is-active'));
            card.classList.add('is-active');
            return;
        }

        // CASE C: Multiple cards — loop with clones
        carousel.classList.remove('is-single');
        isLooping = true;
        activeCloneCount = Math.min(visible.length, 3);

        for (let i = 0; i < activeCloneCount; i++) {
            track.appendChild(prepareClone(visible[i % visible.length]));
        }
        for (let i = 0; i < activeCloneCount; i++) {
            const idx = (visible.length - 1 - i + visible.length) % visible.length;
            track.insertBefore(prepareClone(visible[idx]), track.firstChild);
        }

        allCards = [...track.querySelectorAll('.model-card')];
        // Find the first visible (non-clone, non-filtered) card
        const firstVisibleIdx = allCards.findIndex(
            (c) => !c.classList.contains('model-card--clone') && !c.classList.contains('is-filtered-out')
        );
        currentIndex = firstVisibleIdx >= 0 ? firstVisibleIdx : activeCloneCount;
        goToSlide(currentIndex, false);
    }

    // Init
    goToSlide(activeCloneCount, false);
}
